import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import ClearIcon from '@mui/icons-material/Clear';

import { AccessTypeT } from 'business/definitions';
import { StyButton } from '../../../ui/StyButtonUserForm';

/**
 * Gets the icon to display at the start of the button according to the access type
 * @param {AccessTypeT} type - The access type managed by the button
 * @returns A ReactNode to display at the start of the button
 */
const getStartIcon = (type: AccessTypeT): React.ReactNode => {
    switch (type) {
        case 'full_access':
            return <CheckIcon color="success" />;
        case 'restricted':
            return <LockIcon color="warning" />;
        case 'no_access':
            return <ClearIcon color="error" />;
        default:
            return null;
    }
};

/**
 * Get the color of the button according to the access type
 * @param {AccessTypeT} type - The access type managed by the button
 * @returns {string} The palette to pick in the theme to color the button
 */
const getButtonColor = (
    type: AccessTypeT,
): 'success' | 'error' | 'warning' | 'primary' => {
    switch (type) {
        case 'full_access':
            return 'success';
        case 'restricted':
            return 'warning';
        case 'no_access':
            return 'error';
        default:
            return 'primary';
    }
};

interface AccessTypeButtonProps {
    type: AccessTypeT;
    onClick: () => void;
    selected: boolean;
    disabled?: boolean;
    tooltipTitle?: string;
}

const AccessTypeButton = ({
    type,
    onClick,
    disabled = false,
    selected,
    tooltipTitle,
}: AccessTypeButtonProps) => {
    const { t } = useTranslation();

    const children = (
        <StyButton
            data-testid={`e2e-access-type-button-${type}`}
            variant="outlined"
            disabled={disabled}
            buttonStyle={getButtonColor(type)}
            onClick={onClick}
            selected={selected}
            startIcon={getStartIcon(type)}
        >
            {t(`adminSpace.permissionsPanel.${type}`)}
        </StyButton>
    );
    if (!tooltipTitle) return children;
    return (
        <Tooltip key={type} title={tooltipTitle}>
            {children}
        </Tooltip>
    );
};

export default AccessTypeButton;
